import type { SectionCfg, SectionSpec } from "./types";
import { SECTION_DATA_KEY } from "./pageRegistry";
import type { HasData } from "./useCreatorDataFromDB";

/** Inline path prefix for per-section shell fields (`section.home/hero.heading`). */
export function sectionContentEditPrefix(
  pageKey: string,
  sectionId: string,
): string {
  return `section.${pageKey}/${sectionId}.`;
}

const TOP_LEVEL_FIELDS = new Set([
  "heading",
  "lead",
  "eyebrow",
  "subtitle",
  "productsHeading",
  "productsEyebrow",
  "body",
  "rich",
  "quote",
  "author",
  "label",
  "sub",
  "cta",
  "buttonLabel",
  "placeholder",
  "privacyNote",
  "address",
  "caption",
]);

function patchPostTitlesField(
  cfg: SectionCfg,
  index: number,
  raw: string,
): SectionCfg {
  if (!Number.isFinite(index) || index < 0) return cfg;
  const prev = Array.isArray(cfg.postTitles)
    ? cfg.postTitles.map((v) => String(v ?? ""))
    : [];
  const next = prev.slice();
  while (next.length <= index) next.push("");
  next[index] = raw;
  return { ...cfg, postTitles: next };
}

function patchItemsField(
  cfg: SectionCfg,
  itemKey: string,
  field: string,
  raw: string,
): SectionCfg {
  if (!Array.isArray(cfg.items)) return cfg;
  let hit = false;
  const items = cfg.items.map((it, i) => {
    if (!it || typeof it !== "object") return it;
    const id = String((it as { id?: string }).id || i);
    if (id !== itemKey && String(i) !== itemKey) return it;
    hit = true;
    return { ...it, [field]: raw };
  });
  return hit ? { ...cfg, items } : cfg;
}

function patchNestedChildField(
  cfg: SectionCfg,
  childId: string,
  field: string,
  raw: string,
): SectionCfg {
  let hit = false;
  const next: SectionCfg = { ...cfg };

  const patchKids = (children: SectionCfg[]) =>
    children.map((ch) => {
      if (!ch || String(ch.id) !== childId) return ch;
      hit = true;
      const itemMatch = /^items\.([^.]+)\.(.+)$/.exec(field);
      if (itemMatch) {
        return patchItemsField(ch, itemMatch[1], itemMatch[2], raw);
      }
      return { ...ch, [field]: raw };
    });

  if (Array.isArray(cfg.columns)) {
    next.columns = cfg.columns.map((col) => {
      if (!col || typeof col !== "object") return col;
      const children = Array.isArray(col.children) ? col.children : [];
      const kids = patchKids(children as SectionCfg[]);
      return kids !== children ? { ...col, children: kids } : col;
    });
  }

  if (Array.isArray(cfg.tabs)) {
    next.tabs = cfg.tabs.map((tab) => {
      if (!tab || typeof tab !== "object") return tab;
      const children = Array.isArray(tab.children) ? tab.children : [];
      const kids = patchKids(children as SectionCfg[]);
      return kids !== children ? { ...tab, children: kids } : tab;
    });
  }

  return hit ? next : cfg;
}

const ITEM_TEXT_FIELDS = new Set([
  "quote",
  "name",
  "q",
  "a",
  "label",
  "code",
  "message",
  "platform",
  "count",
  "caption",
  "brand",
  "desc",
  "ctaLabel",
]);

export function mergeSectionCfgFromContentEdits(
  pageKey: string,
  sectionId: string,
  cfg: SectionCfg,
  contentEdits: Record<string, string> | undefined,
): SectionCfg {
  if (!contentEdits) return cfg;
  const prefix = sectionContentEditPrefix(pageKey, sectionId);
  let next = { ...cfg };
  for (const [key, raw] of Object.entries(contentEdits)) {
    if (!key.startsWith(prefix)) continue;
    const field = key.slice(prefix.length);
    const childMatch = /^child\.([^.]+)\.(.+)$/.exec(field);
    if (childMatch) {
      const [, childId, childField] = childMatch;
      const itemMatch = /^items\.([^.]+)\.(.+)$/.exec(childField);
      if (itemMatch) {
        if (!ITEM_TEXT_FIELDS.has(itemMatch[2])) continue;
        next = patchNestedChildField(next, childId, childField, raw);
        continue;
      }
      if (!TOP_LEVEL_FIELDS.has(childField) && childField !== "message") {
        continue;
      }
      next = patchNestedChildField(next, childId, childField, raw);
      continue;
    }
    const itemMatch = /^items\.([^.]+)\.(.+)$/.exec(field);
    if (itemMatch) {
      if (!ITEM_TEXT_FIELDS.has(itemMatch[2])) continue;
      next = patchItemsField(next, itemMatch[1], itemMatch[2], raw);
      continue;
    }
    const postTitleMatch = /^postTitles\.(\d+)$/.exec(field);
    if (postTitleMatch) {
      next = patchPostTitlesField(next, Number(postTitleMatch[1]), raw);
      continue;
    }
    if (TOP_LEVEL_FIELDS.has(field)) {
      next = { ...next, [field]: raw };
    }
  }
  return next;
}

export function sectionHiddenOnPublicSite(
  pageKey: string,
  section: SectionSpec,
  contentEdits: Record<string, string> | undefined,
  hasData?: HasData,
): boolean {
  if (!contentEdits) return false;
  const headingKey = `${sectionContentEditPrefix(pageKey, section.id)}heading`;
  if (!Object.prototype.hasOwnProperty.call(contentEdits, headingKey)) {
    return false;
  }
  if (String(contentEdits[headingKey] ?? "").trim() !== "") return false;

  const dataKey = SECTION_DATA_KEY[section.type];
  if (dataKey != null && hasData && hasData[dataKey] !== true) {
    return true;
  }
  return false;
}
